import { useEffect, useState } from 'react'
import { Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { ISimpleRelatedCourse } from 'interfaces/learningPath'
import { RankmiTable } from '@rankmi/rankmi-table'
import { Modal } from 'components/reusables/modal'
import { ModalButtons } from 'components/reusables/modalButtons'
import { TableSkeleton } from 'components/reusables/skeletons'
import Prerequisites from 'components/reusables/prerequisites'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBook } from '@fortawesome/pro-solid-svg-icons/faBook'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle'
import { learningPathsService } from 'api/administration/learningPaths'
import { CourseLmsProfileType } from 'enum/course-lms-profile-type.enum'
import { Wrapper, Column, TooltipTitle } from './styled'

interface IProps {
  id: number
  name: string
  close: () => void
}

const Details = ({ id, name, close }: IProps) => {
  const { t } = useTranslation()
  const prefix =
    'administration.learningPaths.manage.create.content.prerequisites.details'
  const [courses, setCourses] = useState<ISimpleRelatedCourse[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getCourses()
  }, [])

  const getCourses = async () => {
    setLoading(true)
    await learningPathsService.getCoursesByLearningPath(id).then((response) => {
      setCourses(response)
    })
    setLoading(false)
  }

  const locale = {
    header: {
      rowsCount: t(`${prefix}.rowsCount`),
      rowsCount_plural: t(`${prefix}.rowsCountPlural`),
    },
  }

  const columns = [
    {
      title: t(`${prefix}.columns.name`),
      render: (data: ISimpleRelatedCourse) => data.course.name,
    },
    {
      title: t(`${prefix}.columns.prerequisites`),
      width: 245,
      render: (data: ISimpleRelatedCourse) => (
        <Prerequisites items={data.prerequisites.map((item) => item.name)} />
      ),
    },
    {
      title: (
        <Column>
          {t(`${prefix}.columns.required.title`)}
          <Tooltip
            title={
              <TooltipTitle>
                {t(`${prefix}.columns.required.description`)}
                <a
                  href="https://rankmi-support.freshdesk.com/es/support/solutions/articles/72000626839-antes-de-partir"
                  target="_blank"
                >
                  {t(`${prefix}.columns.required.descriptionSuffix`)}
                </a>
              </TooltipTitle>
            }
            placement="top"
          >
            <FontAwesomeIcon icon={faInfoCircle} />
          </Tooltip>
        </Column>
      ),
      width: 139,
      render: (data: ISimpleRelatedCourse) =>
        data.required === CourseLmsProfileType.Required
          ? t(`${prefix}.columns.required.options.required`)
          : t(`${prefix}.columns.required.options.optional`),
    },
  ]

  return (
    <Modal
      title={name}
      actions={
        <ModalButtons
          secondaryText={t(`${prefix}.buttons.close`)}
          secondaryAction={close}
        />
      }
      maxheight={400}
      maxwidth={800}
      close={close}
    >
      <Wrapper>
        {loading ? (
          <TableSkeleton />
        ) : (
          <RankmiTable
            locale={locale}
            columnHeaders={columns}
            data={courses}
            total={courses.length}
            countIcon={faBook}
          />
        )}
      </Wrapper>
    </Modal>
  )
}

export default Details
